import { useRef, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Card,
  CardContent,
  Button,
  Textarea,
  Progress,
  ReversePaginatedList,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Send, Paperclip, ArrowLeft, X, FileIcon, Loader2 } from "lucide-react";
import type { ChatRoom, ChatAttachment } from "@tellescope/types-models";
import { useUploadTellescopeFile } from "@tellescope/tanstack-query";
import { useEnduserChatMessages } from "../../hooks/useEnduserChatMessages";
import { useSendChatMessage } from "../../hooks/useSendChatMessage";
import { useEnduserCurrentEnduser, useEnduserStore } from "../../store/hooks";
import { MessageBubble, MessagesSkeleton, EmptyMessages } from "../chat";
import { getAttachmentType } from "@tellescope/utils";

interface ChatDialogProps {
  chatRoom: ChatRoom | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PendingAttachment {
  file: File;
  name: string;
}

export const ChatDialog = ({ chatRoom, open, onOpenChange }: ChatDialogProps) => {
  const { t } = useTranslation("messages");
  const currentEnduser = useEnduserCurrentEnduser();
  const session = useEnduserStore((state) => state.session);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Message input state
  const [messageText, setMessageText] = useState("");
  const [pendingAttachment, setPendingAttachment] = useState<PendingAttachment | null>(null);

  // Hooks
  const { messages, isLoading, error } = useEnduserChatMessages({
    roomId: chatRoom?.id ?? "",
    enabled: open && !!chatRoom,
  });

  const { sendMessageAsync, isPending: isSending } = useSendChatMessage();

  const {
    uploadFileAsync,
    stage: uploadStage,
    progress: uploadProgress,
    reset: resetUpload,
  } = useUploadTellescopeFile({
    session,
  });

  // Wrap onOpenChange to reset state when dialog closes
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setMessageText("");
        setPendingAttachment(null);
        resetUpload();
      }
      onOpenChange(newOpen);
    },
    [onOpenChange, resetUpload]
  );

  const isUploading = uploadStage === "preparing" || uploadStage === "uploading" || uploadStage === "confirming";
  const canSend = (messageText.trim() || pendingAttachment) && !isSending && !isUploading;

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Just store the file - upload happens on send
    setPendingAttachment({
      file,
      name: file.name,
    });

    // Reset file input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleRemoveAttachment = useCallback(() => {
    setPendingAttachment(null);
    resetUpload();
  }, [resetUpload]);

  const handleSend = useCallback(async () => {
    if (!chatRoom?.id || !canSend) return;

    try {
      let attachments: ChatAttachment[] | undefined;

      // Upload file if we have a pending attachment
      if (pendingAttachment) {
        const uploadResult = await uploadFileAsync(
          {
            name: pendingAttachment.name,
            size: pendingAttachment.file.size,
            type: pendingAttachment.file.type,
          },
          pendingAttachment.file
        );

        attachments = [
          {
            type: getAttachmentType(pendingAttachment.file.type),
            name: pendingAttachment.name,
            secureName: uploadResult.secureName,
          },
        ];
      }

      const message = messageText.trim() || (pendingAttachment ? t("Sent an attachment") : "");
      if (!message && !attachments) return;

      await sendMessageAsync({
        roomId: chatRoom.id,
        message,
        attachments,
      });

      // Clear input and attachment on success
      setMessageText("");
      setPendingAttachment(null);
      resetUpload();
    } catch {
      // Error is handled by the mutation
    }
  }, [chatRoom?.id, canSend, messageText, pendingAttachment, t, uploadFileAsync, sendMessageAsync, resetUpload]);

  const displayTitle = chatRoom?.title || chatRoom?.topic || t("Conversation");

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg h-[85vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-4 py-3 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 md:hidden" onClick={() => handleOpenChange(false)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <DialogTitle className="text-base font-medium">{displayTitle}</DialogTitle>
          </div>
        </DialogHeader>

        {/* Messages area */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <MessagesSkeleton />
          ) : error ? (
            <div className="p-4">
              <Card className="border-destructive">
                <CardContent className="p-4 text-center text-destructive text-sm">
                  {t("Failed to load messages")}
                </CardContent>
              </Card>
            </div>
          ) : messages.length === 0 ? (
            <EmptyMessages />
          ) : (
            <ReversePaginatedList className="h-full p-4">
              {messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isOwnMessage={!!currentEnduser?.id && message.senderId === currentEnduser.id}
                />
              ))}
            </ReversePaginatedList>
          )}
        </div>

        {/* Reply input */}
        <div className="border-t p-3 flex-shrink-0 bg-background">
          {/* Attachment preview */}
          {pendingAttachment && (
            <div className="mb-2 p-2 bg-muted rounded-lg">
              <div className="flex items-center gap-2">
                <FileIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm truncate flex-1">{pendingAttachment.name}</span>
                {isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={handleRemoveAttachment}
                    title={t("Remove attachment")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {isUploading && <Progress value={uploadProgress} className="mt-2 h-1" />}
            </div>
          )}

          {/* Input row */}
          <div className="flex gap-2">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelect}
              disabled={isUploading || isSending}
            />

            {/* Attach button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-10 w-10 p-0 flex-shrink-0"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || isSending || !!pendingAttachment}
              title={t("Attach file")}
            >
              <Paperclip className="h-4 w-4 text-muted-foreground" />
            </Button>

            {/* Message input */}
            <Textarea
              placeholder={t("Type a message...")}
              className="min-h-[40px] max-h-[120px] resize-none"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              disabled={isSending || isUploading}
              rows={1}
            />

            {/* Send button */}
            <Button
              size="sm"
              className="h-10 w-10 p-0 flex-shrink-0"
              onClick={handleSend}
              disabled={!canSend}
              title={t("Send message")}
            >
              {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
